/**
 * Toolbar icon button with a hover tooltip on web (label + optional
 * shortcut hint, e.g. "Duplicate · ⌘D"). Native keeps the bare Pressable —
 * hover doesn't exist there and the accessibilityLabel already carries the
 * name for screen readers.
 *
 * @module FloorPlanEditorToolbarToolButton
 * @category Containers
 */

import { Platform, Pressable } from 'react-native';
import type { LucideIcon } from 'lucide-react-native';

import { cn } from '@utils/cn';

import { Icon } from '@components/ui/icon';
import { Text } from '@components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@components/ui/tooltip';

type Props = {
  /** Button icon. */
  icon: LucideIcon;
  /** Translated control name — tooltip body + accessibility label. */
  label: string;
  /** Optional shortcut hint appended to the tooltip ("⌘D"). */
  hint?: string;
  /** Fired when the button is pressed. */
  onPress: () => void;
  /** Disables the button. */
  disabled?: boolean;
  /** solid = filled primary (active mode / save); soft = tinted toggle-on. */
  highlight?: 'solid' | 'soft';
  /** Icon tint override (e.g. text-destructive for Delete). */
  iconClassName?: string;
};

const isWeb = Platform.OS === 'web';

/**
 * Toolbar icon button with a hover tooltip on web (label + optional
 * shortcut hint); native keeps the bare Pressable.
 *
 * @category Containers
 * @param icon - Button icon.
 * @param label - Translated control name.
 * @param hint - Optional shortcut hint appended to the tooltip.
 * @param onPress - Fired when the button is pressed.
 * @param disabled - Disables the button.
 * @param highlight - `solid` = filled primary; `soft` = tinted toggle-on.
 * @param iconClassName - Icon tint override.
 */
const FloorPlanToolButton = ({
  icon,
  label,
  hint,
  onPress,
  disabled = false,
  highlight,
  iconClassName,
}: Props) => {
  const button = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled}
      onPress={onPress}
      className={cn(
        'h-10 w-10 items-center justify-center rounded-lg',
        highlight === 'solid'
          ? 'bg-primary'
          : highlight === 'soft'
            ? 'bg-primary/15'
            : 'bg-muted',
        disabled && 'opacity-40',
      )}>
      <Icon
        as={icon}
        size={18}
        className={cn(
          highlight === 'solid'
            ? 'text-primary-foreground'
            : highlight === 'soft'
              ? 'text-primary'
              : 'text-foreground',
          iconClassName,
        )}
      />
    </Pressable>
  );

  if (!isWeb) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>
        <Text className="text-primary-foreground">
          {label}
          {hint !== undefined ? ` · ${hint}` : ''}
        </Text>
      </TooltipContent>
    </Tooltip>
  );
};

export default FloorPlanToolButton;
